import React, { useContext, createContext } from 'react';

const CURRENCY_CONFIG = {
  USD: { code: 'USD', symbol: '$', locale: 'en-US' },
  CAD: { code: 'CAD', symbol: '$', locale: 'en-CA' },
  GBP: { code: 'GBP', symbol: '£', locale: 'en-GB' },
};

const DEFAULT_CURRENCY = 'USD';

export const CurrencyContext = createContext(CURRENCY_CONFIG[DEFAULT_CURRENCY]);

/**
 * Returns currency config for the current company.
 *
 * Provides: { code, symbol, locale, format(amount) }
 *
 * Wrap your component tree with <CurrencyProvider currency={company.currency} />
 * to get company-specific values; without a provider it falls back to USD.
 */
export function useCurrency() {
  const config = useContext(CurrencyContext);

  function format(amount, options = {}) {
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: config.code,
      ...options,
    }).format(amount);
  }

  return { ...config, format };
}

/**
 * Provider component. Place near the root of any subtree that needs
 * company-aware currency formatting.
 *
 * @param {string} currency - One of "USD", "CAD", "GBP". Defaults to "USD".
 */
export function CurrencyProvider({ currency, children }) {
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG[DEFAULT_CURRENCY];
  return (
    <CurrencyContext.Provider value={config}>
      {children}
    </CurrencyContext.Provider>
  );
}
